import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const faqData = [
  {
    cat: 'Shopping & Collections',
    items: [
      { q: 'What types of furniture does Eclectic Home offer?', a: 'We carry 500+ handpicked pieces across five collections: Living Room (sofas, sectionals, coffee tables, accent chairs), Bedroom (bed frames, nightstands, dressers), Dining Room (dining tables, chairs, sideboards), Home Office (desks, chairs, bookshelves), and Outdoor (patio sets, lounge seating, dining). All pieces are personally selected for quality and design.' },
      { q: 'Where can I buy modern furniture near me in Las Vegas?', a: 'Eclectic Home is your premium modern furniture store in Las Vegas, NV. Visit our 10,000 sq ft showroom on the Las Vegas Strip corridor to experience our collections in person. Our certified design consultants are available daily to help you find the perfect pieces for your home.' },
      { q: 'Can I see furniture in person before buying?', a: 'Absolutely. We encourage it. Our Las Vegas showroom is open Mon–Fri 10am–7pm, Sat 10am–6pm, and Sun 11am–5pm. Most of our collection is on display — you can sit, feel, and experience each piece. Our team is available to answer questions and help with decisions.' },
      { q: 'Do you offer custom furniture orders?', a: 'Yes. Many of our pieces are available in custom upholstery colors, fabric choices, sizes, and wood finishes. Custom orders typically take 6–14 weeks depending on the manufacturer. Ask our design team during your consultation to explore customization options for your specific needs.' },
    ],
  },
  {
    cat: 'Pricing & Value',
    items: [
      { q: 'How much does quality furniture cost?', a: 'Premium furniture varies by category and complexity. Accent chairs start from $800, sofas from $1,800, dining tables from $1,200, and bed frames from $1,500. We carry pieces across multiple price points — all selected to offer genuine value relative to their quality. Free white-glove delivery is included on orders over $1,500.' },
      { q: 'What makes Eclectic Home furniture high quality?', a: 'Every piece in our collection passes our rigorous quality criteria: solid wood or premium metal frames, upholstery fabrics rated for 100,000+ double-rubs, hand-finished surfaces, and honest construction. We visit manufacturers personally, and we only carry what we\'d put in our own homes.' },
      { q: 'Do you price match or offer sales?', a: 'We occasionally offer seasonal promotions and new arrival specials. We\'re proud of our pricing — we don\'t inflate prices to "discount" them. We believe in transparent, fair pricing for genuinely exceptional furniture. Sign up for our newsletter to be notified of sales and exclusive events.' },
    ],
  },
  {
    cat: 'Delivery & Setup',
    items: [
      { q: 'How does delivery work?', a: 'We offer free white-glove delivery on all orders over $1,500 throughout Las Vegas and surrounding areas. Our delivery team contacts you to schedule a convenient time, delivers your furniture to the specific room you choose, assembles it completely, and removes all packaging materials. No boxes, no assembly required.' },
      { q: 'How long does delivery take?', a: 'In-stock items are typically delivered within 1–2 weeks depending on your location and scheduling. Custom orders take 6–14 weeks from order confirmation. We\'ll provide a clear timeline at the time of purchase and keep you updated throughout the process.' },
      { q: 'What is your return policy?', a: 'We offer a 60-day satisfaction guarantee on all in-stock furniture purchases. If you\'re not fully satisfied, we\'ll work with you to find the right solution — exchange, store credit, or return. Custom orders are final sale. Our goal is that you absolutely love what you buy.' },
    ],
  },
  {
    cat: 'Design Services',
    items: [
      { q: 'How do I choose the right furniture for my space?', a: 'Start with your room dimensions, ceiling height, and how you use the space. Consider the natural light, existing architectural details, and your lifestyle. Our certified design consultants offer a complimentary 1-hour session — in showroom or virtual — to guide you through selections that work together beautifully and practically.' },
      { q: 'Is the design consultation really free?', a: 'Yes, completely free with no purchase obligation. Our consultants genuinely enjoy helping clients build spaces they love — whether that results in a single chair purchase or a whole-home furnishing. Schedule yours by calling us or filling out our contact form.' },
    ],
  },
];

export default function FaqPage() {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="min-w-[1024px]">
      <Navbar />
      <main>
        <section className="bg-dark pt-44 pb-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">Help &amp; Info</span>
              <span className="w-8 h-px bg-gold" />
            </div>
            <h1 className="font-serif text-6xl font-bold text-white mb-5">FAQ</h1>
            <p className="text-white/50 text-lg">
              Everything you need to know about our furniture collections, delivery, and design services.
            </p>
          </div>
        </section>

        <section className="py-24 bg-white" itemScope itemType="https://schema.org/FAQPage">
          <div className="max-w-4xl mx-auto px-6">
            {faqData.map((cat) => (
              <div key={cat.cat} className="mb-14">
                <h2 className="font-serif text-2xl font-bold text-dark mb-6 pb-3 border-b-2 border-gold inline-block">
                  {cat.cat}
                </h2>
                <div className="space-y-3">
                  {cat.items.map((item, i) => {
                    const key = `${cat.cat}-${i}`;
                    const isOpen = open === key;
                    return (
                      <div key={key} itemScope itemProp="mainEntity" itemType="https://schema.org/Question"
                        className={`rounded-xl overflow-hidden border transition-colors ${isOpen ? 'border-gold/40 bg-cream-DEFAULT' : 'border-cream-dark hover:border-gold/20'}`}>
                        <button onClick={() => setOpen(isOpen ? null : key)}
                          className="w-full flex items-center justify-between p-6 text-left cursor-pointer">
                          <h3 itemProp="name" className={`font-semibold pr-4 text-sm leading-relaxed ${isOpen ? 'text-dark' : 'text-dark/70'}`}>{item.q}</h3>
                          <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${isOpen ? 'bg-gold text-white' : 'bg-cream-mid text-dark/40'}`}>
                            <i className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                          </div>
                        </button>
                        {isOpen && (
                          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-6">
                            <p itemProp="text" className="text-dark/60 text-sm leading-relaxed">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="bg-dark rounded-2xl p-10 text-center">
              <h2 className="font-serif text-2xl font-bold text-white mb-3">Still Have Questions?</h2>
              <p className="text-white/50 mb-8 text-sm">Our design team is happy to help with any specific questions.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="bg-gold text-white px-8 py-4 rounded font-bold text-xs uppercase tracking-wider cursor-pointer whitespace-nowrap hover:bg-gold-dark transition-colors">
                  Ask a Question
                </Link>
                <a href="tel:7028509900" className="border border-white/20 text-white px-8 py-4 rounded font-bold text-xs uppercase tracking-wider cursor-pointer whitespace-nowrap hover:border-gold hover:text-gold transition-colors">
                  Call (702) 850-9900
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
